import { Users, Shield, AlertTriangle, Heart, Ban, Flag } from "lucide-react";

export default function Community() {
  return (
    <main data-testid="community-page" className="pt-24 md:pt-32 pb-20">
      <div className="max-w-screen-md mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-400 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30">
              <Users className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs font-medium text-purple-400 uppercase tracking-widest">
              Documento Legale
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
            Community Guidelines
          </h1>
          <p className="text-purple-200/50">
            Ultimo aggiornamento: Gennaio 2026
          </p>
        </div>

        {/* Intro */}
        <div className="bg-purple-500/10 border border-purple-500/20 rounded-2xl p-6 mb-12">
          <p className="text-purple-200/80 leading-relaxed">
            Nerbys è una piattaforma di social discovery basata sulla prossimità. 
            Queste Community Guidelines definiscono i comportamenti e i contenuti consentiti e vietati, 
            per mantenere la community sicura e affidabile. L'uso dell'App implica il rispetto dei Termini di Servizio.
          </p>
        </div>

        {/* Content */}
        <div className="space-y-10">
          <section className="legal-section" data-testid="community-principi">
            <h2 className="text-2xl font-bold text-white mb-4">
              1. Principi base
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Heart className="w-5 h-5 text-pink-400" />
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Rispetto</p>
                  <p className="text-purple-200/60 text-sm">Tratta gli altri utenti con educazione e senza molestie.</p>
                </div>
              </div>
              
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Autenticità</p>
                  <p className="text-purple-200/60 text-sm">Usa un profilo genuino e informazioni veritiere.</p>
                </div>
              </div>
              
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Sicurezza</p>
                  <p className="text-purple-200/60 text-sm">Niente stalking, minacce o comportamenti a rischio.</p>
                </div>
              </div>
              
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-white font-semibold mb-1">Privacy</p>
                  <p className="text-purple-200/60 text-sm">Non condividere dati personali altrui senza consenso.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="legal-section" data-testid="community-vietati">
            <h2 className="text-2xl font-bold text-white mb-4">
              2. Contenuti vietati
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>Sono vietati contenuti o comportamenti che includono, a titolo esemplificativo:</p>
              
              <div className="space-y-3">
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                  <p className="text-red-400 font-semibold mb-1">Impersonation e profili falsi</p>
                  <p className="text-purple-200/60">Fingere di essere un'altra persona, usare foto di terzi senza consenso, creare identità ingannevoli.</p>
                </div>
                
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                  <p className="text-red-400 font-semibold mb-1">Molestie e minacce</p>
                  <p className="text-purple-200/60">Intimidazioni, ricatti, odio, bullismo, stalking, incitamento alla violenza.</p>
                </div>
                
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                  <p className="text-red-400 font-semibold mb-1">Contenuti illegali</p>
                  <p className="text-purple-200/60">Qualsiasi contenuto o condotta contraria alla legge applicabile.</p>
                </div>
                
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                  <p className="text-red-400 font-semibold mb-1">Contenuti sessualmente espliciti/pornografici</p>
                  <p className="text-purple-200/60">Nudità esplicita, atti sessuali, materiale pornografico o sessualizzazione aggressiva.</p>
                </div>
                
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                  <p className="text-red-400 font-semibold mb-1">Contenuti che coinvolgono minori</p>
                  <p className="text-purple-200/60">È vietato pubblicare o promuovere contenuti che ritraggono o sfruttano minori in modo inappropriato; qualsiasi sospetto sarà trattato con massima priorità.</p>
                </div>
                
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                  <p className="text-red-400 font-semibold mb-1">Hate speech</p>
                  <p className="text-purple-200/60">Contenuti che attaccano o discriminano persone/gruppi per caratteristiche protette (es. razza, origine, religione, orientamento, disabilità, genere).</p>
                </div>
                
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                  <p className="text-red-400 font-semibold mb-1">Doxxing e violazione della privacy</p>
                  <p className="text-purple-200/60">Pubblicare dati personali di altri (telefono, indirizzo, luogo di lavoro, ecc.) o raccoglierli senza autorizzazione.</p>
                </div>
                
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                  <p className="text-red-400 font-semibold mb-1">Spam e attività commerciali non autorizzate</p>
                  <p className="text-purple-200/60">Promozioni, pubblicità, link massivi, schemi fraudolenti.</p>
                </div>
                
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                  <p className="text-red-400 font-semibold mb-1">Automazioni e scraping</p>
                  <p className="text-purple-200/60">Bot, script, raccolta sistematica di dati, reverse engineering, accessi non autorizzati.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="legal-section" data-testid="community-profilo">
            <h2 className="text-2xl font-bold text-white mb-4">
              3. Regole sul profilo e sui link social
            </h2>
            <div className="text-purple-200/70 text-sm leading-relaxed">
              <p>
                Il profilo deve rappresentarti in modo veritiero. I link social devono riferirsi a profili 
                che controlli o per cui hai pieno diritto di condivisione. Non inserire link o informazioni 
                che promuovono truffe, spam, contenuti illegali o violazioni dei diritti altrui.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="community-sicurezza">
            <h2 className="text-2xl font-bold text-white mb-4">
              4. Sicurezza e incontri nella vita reale
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                Nerbys facilita contatti tra persone: gli incontri offline comportano rischi. Consigliamo:
              </p>
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-5">
                <ul className="list-disc list-inside space-y-2">
                  <li><strong className="text-yellow-400">Incontrarsi in luoghi pubblici</strong> e informare una persona fidata.</li>
                  <li><strong className="text-yellow-400">Evitare di condividere informazioni sensibili</strong> troppo presto.</li>
                  <li><strong className="text-yellow-400">Interrompere ogni interazione</strong> se ti senti a disagio.</li>
                  <li><strong className="text-yellow-400">Usare gli strumenti di blocco e segnalazione</strong> disponibili nell'App.</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="legal-section" data-testid="community-enforcement">
            <h2 className="text-2xl font-bold text-white mb-4">
              5. Segnalazioni e enforcement
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                Puoi <strong className="text-white">segnalare utenti o contenuti</strong> direttamente dall'App (funzione "Segnala") 
                e <strong className="text-white">bloccare utenti</strong> (funzione "Blocca").
              </p>
              <p>Possiamo adottare provvedimenti quali:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>rimozione di contenuti</li>
                <li>warning</li>
                <li>sospensione temporanea</li>
                <li>ban permanente per violazioni gravi o ripetute</li>
              </ul>
              <p className="mt-4">
                In caso di provvedimenti contestati, puoi richiedere riesame scrivendo a 
                <a href="mailto:support@nerbys.app" className="text-purple-400 hover:underline ml-1">support@nerbys.app</a> indicando 
                email account, nickname e motivo della contestazione.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="community-tolleranza-zero">
            <h2 className="text-2xl font-bold text-white mb-4">
              6. Tolleranza zero
            </h2>
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Ban className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <p className="text-red-400 font-bold mb-2">Esempi di violazioni gravi:</p>
                  <p className="text-purple-200/70 text-sm">
                    Comportamenti come <strong className="text-white">minacce, stalking, violenza, contenuti illegali o 
                    sfruttamento di minori</strong> comportano tipicamente <strong className="text-red-400">rimozione immediata 
                    e ban</strong>, oltre a eventuali segnalazioni alle autorità quando richiesto dalla legge.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="legal-section" data-testid="community-contact">
            <h2 className="text-2xl font-bold text-white mb-4">
              Contatti
            </h2>
            <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-6">
              <div className="space-y-2">
                <p className="text-purple-200/70">
                  <strong className="text-white">Supporto:</strong> <a href="mailto:support@nerbys.app" className="text-purple-400 hover:underline">support@nerbys.app</a>
                </p>
                <p className="text-purple-200/70">
                  <strong className="text-white">Privacy:</strong> <a href="mailto:privacy@nerbys.app" className="text-purple-400 hover:underline">privacy@nerbys.app</a>
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
